import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    FaSpinner, FaPlus, FaArrowLeft, FaEye, FaTrash, FaEdit, FaTimes, FaSyncAlt, FaFilter, FaBarcode, FaMoneyBillWave, FaHandshake
} from 'react-icons/fa';
import { format, parseISO } from 'date-fns';
import DividaModal from './DividaModal';
import { ui } from '../utils/styles';
import RenegociarModal from './RenegociarModal';

// Função auxiliar para formatar a data como "YYYY-MM-DD" (necessário para o input type="date")
const formatarDataInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Função para calcular o primeiro e o último dia do mês corrente
const getDatasCorrentes = () => {
    const today = new Date();
    const primeiroDia = new Date(today.getFullYear(), today.getMonth(), 1);
    const ultimoDia = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
        inicio: formatarDataInput(primeiroDia),
        fim: formatarDataInput(ultimoDia),
    };
};

// Adicione esta função auxiliar em algum lugar fora do componente DividasScreen:
// --- VERSÃO 1: Se você SÓ usa vírgula decimal (como no seu CSV) ---
const convertBRLToNumber = (brlString) => {
    if (!brlString) return 0;
    let str = brlString.toString();

    // Troca APENAS a vírgula (decimal) por ponto
    str = str.replace(/,/g, '.');

    return Number(str) || 0;
};

const DividasScreen = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const devedor = location.state?.devedor;
    const clientesContratantes = location.state?.listaClientes;
    const { inicio, fim } = getDatasCorrentes(); // Pega as datas do mês corrente
    const [allDates, setAllDates] = useState(true);
    const [allDividas, setAllDividas] = useState([]);
    const [loading, setLoading] = useState(false);

    // Emitente
    const emitente = useMemo(
        () => localStorage.getItem('userIdEmitente'),
        []
    );

    // --- ESTADOS DE FILTRO ---
    // Inicializa com as datas do mês corrente
    const [filterDataInicio, setFilterDataInicio] = useState(inicio);
    const [filterDataFim, setFilterDataFim] = useState(fim);
    // NOVO ESTADO: Inicializa como 'Todos'
    const [filterStatus, setFilterStatus] = useState('Aberto');
    // --- FIM DOS ESTADOS DE FILTRO ---

    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedDivida, setSelectedDivida] = useState(null);

    const [showRenegociarModal, setShowRenegociarModal] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    // const [totalDividasAbertas, setTotalDividasAbertas] = useState(0);

    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const authHeader = useMemo(
        () => ({ Authorization: `Bearer ${localStorage.getItem('userToken')}` }),
        []
    );

    const totalDividasAbertas = useMemo(() => {
        return allDividas.reduce((acc, divida) => {
            if (divida.status === 'Aberto') {
                return acc + convertBRLToNumber(divida.valor_original);
            }
            return acc;
        }, 0);
    }, [allDividas]);

    // Efeito para carregar dívidas na montagem ou mudança de devedor
    useEffect(() => {
        setFilterDataInicio('1900-01-01');
        setFilterDataFim('2500-12-31');
        if (!devedor || !devedor.id) {
            toast.error("Devedor não identificado. Redirecionando.");
            navigate('/devedores');
        } else {
            fetchDividas();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [devedor?.id]);


    // --- Funções de API (fetchDividas AJUSTADA) ---
    const fetchDividas = async () => {
        if (!devedor || !devedor.id) return;

        setLoading(true);

        // Constrói a query de filtro
        const params = new URLSearchParams();
        if (filterDataInicio) params.append('dataVencimentoDe', filterDataInicio);
        if (filterDataFim) params.append('dataVencimentoAte', filterDataFim);

        // NOVO: Adiciona o filtro de Status, exceto se for 'Todos'
        // if (filterStatus !== 'Todos') {
        params.append('status', filterStatus);
        // }

        const queryString = params.toString();

        try {
            const { data } = await axios.get(
                `${URL_BACKEND}/divida/devedor/${devedor.id}${queryString ? `?${queryString}` : ''}`,
                { headers: authHeader }
            );
            const arr = Array.isArray(data) ? data : [];
            setAllDividas(arr);
            console.log(data);

            // --- NOVO CÁLCULO TOTAL ---
            const novoTotal = arr.reduce((acc, divida) => {
                // Apenas dívidas Abertas (se o filtro já não garantiu isso)
                if (divida.status === 'Aberto') {
                    return acc + convertBRLToNumber(divida.valor_original);
                    // return acc + divida.valor_original;
                }
                return acc;
            }, 0);

            // setTotalDividasAbertas(novoTotal);
            // --- FIM DO CÁLCULO ---

            // toast.success(`Dívidas carregadas: ${arr.length} registros.`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erro ao carregar dívidas.');
            setAllDividas([]);
        } finally {
            setLoading(false);
        }
    };

    // --- Outras Lógicas (CRUD, formatação, etc. - mantidas) ---

    const handleCreate = () => { setModalMode('create'); setSelectedDivida(null); setShowModal(true); };
    const handleEdit = (divida) => { setModalMode('edit'); setSelectedDivida(divida); setShowModal(true); };
    const handleView = (divida) => { setModalMode('view'); setSelectedDivida(divida); setShowModal(true); };

    const handleDelete = async (id) => {
        if (!window.confirm(`Confirma a exclusão da dívida ID ${id}?`)) return;
        const toastId = toast.loading('Excluindo...');
        try {
            await axios.delete(`${URL_BACKEND}/divida/${id}`, { headers: authHeader });
            toast.success('Dívida excluída!', { id: toastId });
            fetchDividas();
        } catch (error) {
            toast.error('Erro ao excluir dívida.', { id: toastId });
        }
    };


    const handleGerarBoleto = async (divida) => {
        if (!window.confirm(`Confirma envio para o faturamento dessa dívida ?`)) return;
        const payload = {
            "id_divida": Number(divida.id) || 0,
            "emitenteId": Number(emitente),
            "clienteId": Number(divida.id_cliente),
            "valor": Number(divida.valor_original),
            "vencimento": formatarDataInput(new Date(divida.vencimento)),
            "descricao": (divida.obs || "Faturamento").trim()
        }

        const toastId = toast.loading('Enviando para faturamento...');
        try {
            await axios.post(`${URL_BACKEND}/faturamento/save`, payload, { headers: authHeader });
            toast.success('Lançamento feito com sucesso!');
            await axios.put(`${URL_BACKEND}/divida/baixa/${divida.id}/Pago`, { headers: authHeader });
            toast.success('Dívida baixada!', { id: toastId });
            fetchDividas();
        } catch (error) {
            toast.error('Erro ao enviar faturamento.' + error);
        }
    };

    const handleBaixarDivida = async (id) => {
        if (!window.confirm(`Confirma a baixa dessa dívida ID ${id}?`)) return;
        const toastId = toast.loading('Baixando...');
        try {
            await axios.put(`${URL_BACKEND}/divida/baixa/${id}/Pago`, { headers: authHeader });
            toast.success('Dívida baixada!', { id: toastId });
            fetchDividas();
        } catch (error) {
            toast.error('Erro ao baixar dívida.' + error, { id: toastId });
        }
    };


    const formatCurrency = (value) => {
        if (value === null || value === undefined) return 'R$ 0,00';
        return `R$ ${Number(value).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
    };

    if (!devedor) {
        return <div style={{ ...ui.container, textAlign: 'center', padding: '50px' }}><FaSpinner className="fa-spin" /> Carregando Devedor...</div>;
    }

    // Função para limpar todos os filtros
    const handleClearFilters = () => {
        setFilterDataInicio(inicio);
        setFilterDataFim(fim);
        setFilterStatus('Todas');
        setAllDates(false); // <--- Adicione isso
        fetchDividas();
    };

    const handleAllDatesToggle = (e) => {
        const checked = e.target.checked;
        setAllDates(checked);

        if (checked) {
            setFilterDataInicio('1900-01-01');
            setFilterDataFim('2500-12-31');
        } else {
            // Quando desmarcar, volta para o mês corrente padrão
            setFilterDataInicio(inicio);
            setFilterDataFim(fim);
        }
    };

    // E a função para abrir o modal:
    const handleOpenRenegociar = () => {
        if (allDividas.filter(d => d.status === 'Aberto').length === 0) {
            toast.error("Não há dívidas em aberto para renegociar.");
            return;
        }
        setShowRenegociarModal(true);
    };

    // Alternar seleção de um único item
    const toggleSelect = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    // Selecionar/Desmarcar todos (apenas os que estão visíveis e em Aberto)
    const toggleSelectAll = () => {
        const dividasEmAberto = allDividas.filter(d => d.status === 'Aberto').map(d => d.id);

        if (selectedIds.length === dividasEmAberto.length) {
            setSelectedIds([]); // Se todos já estão selecionados, limpa
        } else {
            setSelectedIds(dividasEmAberto); // Caso contrário, seleciona todos em aberto
        }
    };

    return (
        <div style={ui.container}>

            {/* Cabeçalho (mantido) */}
            <div style={{
                ...ui.header,
                background: 'linear-gradient(to right, #0e7490, #047857)',
                padding: '12px 20px',
            }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <h1 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#fff' }}>
                        Dívidas de: **{devedor.nome}**
                    </h1>
                    <p style={{ fontSize: '0.9rem', color: '#eee' }}>
                        Cliente Contratante: {devedor.nome_cliente_contratante} | CPF/CNPJ: {devedor.cnpj || devedor.cpf}
                    </p>
                </div>
                <button
                    onClick={() => navigate('/devedores')}
                    style={{ ...ui.homeButton, background: 'transparent', border: '1px solid rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                    <FaArrowLeft /> Voltar para Devedores
                </button>
            </div>

            {/* --- CARD DE DESTAQUE: SALDO TOTAL --- */}
            <div style={{
                ...ui.tableCard, // Reutilizando um estilo base para o card
                padding: '10px 20px',
                margin: '15px 0',
                backgroundColor: totalDividasAbertas > 0 ? '#fee2e2' : '#d1fae5', // Fundo vermelho claro se houver saldo
                borderLeft: `6px solid ${totalDividasAbertas > 0 ? '#ef4444' : '#059669'}`, // Borda lateral colorida
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <h2 style={{
                    fontSize: '1.4rem',
                    fontWeight: '600',
                    color: '#1f2937'
                }}>
                    Saldo **TOTAL** das Dívidas
                </h2>
                <span style={{
                    fontSize: '1.8rem',
                    fontWeight: 'bold',
                    color: totalDividasAbertas > 0 ? '#ef4444' : '#059669', // Cor do texto (Vermelho ou Verde)
                    marginLeft: '20px',
                }}>
                    {formatCurrency(totalDividasAbertas)}
                </span>
            </div>
            {/* --- FIM DO CARD DE DESTAQUE --- */}

            {/* Ações e Filtros (COMPLETO) */}
            <div style={{ ...ui.filters, justifyContent: 'space-between', flexWrap: 'wrap' }}>

                {/* Seção de Filtros ajustada */}
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', paddingBottom: 8, flexWrap: 'wrap' }}>

                    {/* Filtro de Status (já existente) */}
                    <div style={ui.filterGroup}>
                        <label style={ui.label} htmlFor="status">Status</label>
                        <select id="status" style={ui.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                            <option value="Todas">Todas</option>
                            <option value="Aberto">Aberto</option>
                            <option value="Pago">Pago</option>
                        </select>
                    </div>

                    {/* NOVO: Checkbox Todas as Datas */}
                    <div style={{ ...ui.filterGroup, flexDirection: 'row', alignItems: 'center', gap: 8, paddingBottom: 10 }}>
                        <input
                            type="checkbox"
                            id="allDates"
                            checked={allDates}
                            onChange={handleAllDatesToggle}
                            style={{ cursor: 'pointer', width: 18, height: 18 }}
                        />
                        <label htmlFor="allDates" style={{ ...ui.label, marginBottom: 0, cursor: 'pointer' }}>
                            Todo o Período
                        </label>
                    </div>

                    {/* Filtros de Data - Agora ficam desabilitados se allDates for true */}
                    <div style={ui.filterGroup}>
                        <label style={{ ...ui.label, color: allDates ? '#9ca3af' : '#374151' }} htmlFor="dataInicio">De:</label>
                        <input
                            id="dataInicio"
                            type="date"
                            style={{ ...ui.input, backgroundColor: allDates ? '#f3f4f6' : '#fff' }}
                            value={filterDataInicio}
                            onChange={e => setFilterDataInicio(e.target.value)}
                            disabled={loading || allDates}
                        />
                    </div>

                    <div style={ui.filterGroup}>
                        <label style={{ ...ui.label, color: allDates ? '#9ca3af' : '#374151' }} htmlFor="dataFim">Até:</label>
                        <input
                            id="dataFim"
                            type="date"
                            style={{ ...ui.input, backgroundColor: allDates ? '#f3f4f6' : '#fff' }}
                            value={filterDataFim}
                            onChange={e => setFilterDataFim(e.target.value)}
                            disabled={loading || allDates}
                        />
                    </div>

                    {/* Botões de Filtrar e Limpar (já existentes) */}
                    <button style={{ ...ui.buttonPrimary, background: '#a855f7', height: ui.input.height }} onClick={fetchDividas}>
                        <FaFilter /> Filtrar
                    </button>
                </div>


                {/* Botões de Ação */}
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', paddingBottom: 8 }}>
                    <button
                        style={{
                            ...ui.buttonGreen,
                            background: selectedIds.length > 0 ? '#8b5cf6' : '#a78bfa', // Muda a cor se tiver algo selecionado
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: selectedIds.length > 0 ? 'pointer' : 'not-allowed'
                        }}
                        onClick={() => {
                            if (selectedIds.length === 0) {
                                toast.error("Selecione ao menos uma dívida para renegociar.");
                                return;
                            }
                            setShowRenegociarModal(true);
                        }}
                    >
                        <FaHandshake style={{ fontSize: '1.1rem' }} />
                        Renegociar ({selectedIds.length})
                    </button>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', paddingBottom: 8 }}>
                    <button style={{ ...ui.buttonGreen, background: '#10b981' }} onClick={handleCreate}>
                        <FaPlus /> Lançar Nova Dívida
                    </button>
                </div>


            </div>

            {/* TABELA DE DÍVIDAS (Mantida) */}
            <div style={ui.tableCard}>
                {loading ? (
                    <p style={{ padding: 16 }}>Carregando Dívidas...</p>
                ) : allDividas.length === 0 ? (
                    <p style={{ color: 'red', padding: 16 }}>Nenhuma dívida registrada para este devedor com os filtros aplicados.</p>
                ) : (
                    <div style={ui.tableScroll}>
                        <table style={ui.table}>
                            <thead style={ui.thead}>
                                <tr>
                                    <th style={{ ...ui.th, textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            onChange={toggleSelectAll}
                                            checked={selectedIds.length > 0 && selectedIds.length === allDividas.filter(d => d.status === 'Aberto').length}
                                        />
                                    </th>
                                    <th style={{ ...ui.th, width: '200px' }}>Cliente</th>
                                    <th style={{ ...ui.th, width: '70px' }}>Status</th>
                                    <th style={{ ...ui.th, width: '150px' }}>Valor Original</th>
                                    <th style={{ ...ui.th, width: '120px' }}>Vencimento</th>
                                    <th style={{ ...ui.th, width: '120px' }}>Processo Judicial</th>
                                    <th style={ui.thActions}>Ações</th>

                                </tr>
                            </thead>
                            <tbody>
                                {allDividas.map((d, idx) => (
                                    <tr
                                        key={d.id}
                                        style={{
                                            ...ui.tr,
                                            // Se estiver selecionado, fica azul clarinho, senão faz o zebrado normal
                                            backgroundColor: selectedIds.includes(d.id)
                                                ? '#e0f2fe'
                                                : (idx % 2 === 0 ? '#ffffff' : '#f9fafb')
                                        }}
                                    >


                                        <td style={{ ...ui.tdCenter, textAlign: 'center' }}>
                                            {d.status === 'Aberto' ? (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.includes(d.id)}
                                                    onChange={() => toggleSelect(d.id)}
                                                />
                                            ) : (
                                                <div style={{ width: 13, height: 13, border: '1px solid #ddd', borderRadius: 2, margin: '0 auto', background: '#f5f5f5' }} title="Apenas dívidas em aberto podem ser selecionadas" />
                                            )}
                                        </td>
                                        <td style={ui.tdMono}>#{d.nome}</td>
                                        <td style={ui.tdClamp}>{d.status}</td>
                                        <td style={ui.tdMono}>{formatCurrency(d.valor_original)}</td>
                                        <td style={ui.tdClamp}>
                                            {d.vencimento ? new Date(d.vencimento).toLocaleDateString('pt-BR') : 'N/A'}
                                        </td>
                                        <td style={ui.tdClamp}>{d.id_processo_judicial ? `Proc #${d.id_processo_judicial}` : 'N/A'}</td>
                                        <td style={ui.tdActions}>
                                            <button title="Visualizar" style={ui.actionView} onClick={() => handleView(d)}><FaEye /></button>
                                            <button title="Editar" style={ui.actionEdit} onClick={() => handleEdit(d)}><FaEdit /></button>
                                            <button title="Excluir" style={ui.actionDelete} onClick={() => handleDelete(d.id)}><FaTrash /></button>
                                            <button
                                                title="Enviar para faturamento"
                                                style={{ ...ui.actionEdit, backgroundColor: '#0ea5e9', color: '#000' }} // Cor azul
                                                onClick={() => handleGerarBoleto(d)}
                                            >
                                                <FaBarcode />
                                            </button>

                                            <button
                                                title="Receber Divida"
                                                style={{ ...ui.actionEdit, backgroundColor: '#10b981' }} // Cor verde
                                                onClick={() => handleBaixarDivida(d.id)}
                                            >
                                                <FaMoneyBillWave />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>




            {/* Modais (Mantidos) */}
            <DividaModal
                show={showModal}
                onClose={() => setShowModal(false)}
                divida={selectedDivida}
                onSave={fetchDividas}
                mode={modalMode}
                devedor={devedor}
                clientesContratantes={clientesContratantes}
            />

            <RenegociarModal
                show={showRenegociarModal}
                onClose={() => setShowRenegociarModal(false)}
                devedor={devedor}
                dividasSelecionadas={allDividas.filter(d => selectedIds.includes(d.id))}
                onSave={() => {
                    fetchDividas();
                    setSelectedIds([]); // Limpa a seleção após o sucesso
                }}
            />

        </div>
    );
};

export default DividasScreen;